import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEntitlements } from '../subscription/offers';
import { api } from '../api/client';
import { customFor, loadMedia, removeMedia, saveAudio, saveImage, shrinkImage, useMediaVersion, playCustom } from './media';
import { speak } from '../games/speak';
import type { Word } from './terms';
import { useT } from '../i18n/i18n';
import { usageLine, type MediaUsage } from './usage';

const MAX_RECORD_MS = 10_000;

export function Recorder({ onDone, onCancel }: { onDone: (b: Blob) => void; onCancel: () => void }) {
  const t = useT();
  const [state, setState] = useState<'asking' | 'recording' | 'error'>('asking');
  const [message, setMessage] = useState('');
  const rec = useRef<MediaRecorder | null>(null);
  const stopTimer = useRef<number | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ audio: true }).then((s) => {
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported(t));
      const r = new MediaRecorder(s, type ? { mimeType: type } : undefined);
      const parts: Blob[] = [];
      r.ondataavailable = (e) => { if (e.data.size) parts.push(e.data); };
      r.onstop = () => {
        s.getTracks().forEach((t) => t.stop());
        if (!cancelled) onDone(new Blob(parts, { type: (r.mimeType || 'audio/webm').split(';')[0] }));
      };
      rec.current = r;
      r.start();
      setState('recording');
      stopTimer.current = window.setTimeout(() => r.state === 'recording' && r.stop(), MAX_RECORD_MS);
    }).catch(() => {
      setState('error');
      setMessage(t('The microphone could not be used. Allow it in the browser and try again.'));
    });
    return () => {
      cancelled = true;
      if (stopTimer.current) window.clearTimeout(stopTimer.current);
      if (rec.current?.state === 'recording') rec.current.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (state === 'error') {
    return <span className="md-recording"><span className="field-hint">{message}</span><button type="button" className="btn ghost" onClick={onCancel}>{t('Close')}</button></span>;
  }
  return (
    <span className="md-recording" role="status">
      <span className="md-dot" aria-hidden="true" />
      <span>{state === 'asking' ? t('Starting the microphone…') : t('Recording — say the word')}</span>
      <button type="button" className="btn" disabled={state !== 'recording'} onClick={() => rec.current?.stop()}>{t('Stop')}</button>
      <button type="button" className="btn ghost" onClick={onCancel}>{t('Cancel')}</button>
    </span>
  );
}

function Row({ word, learnerId }: { word: Word; learnerId: string | null }) {
  const t = useT();
  useMediaVersion();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const file = useRef<HTMLInputElement | null>(null);
  const custom = customFor(word.term);
  const photos = word.picture !== false;
  const voices = word.voice !== false;
  const builtIn = word.shown.match(/\p{Extended_Pictographic}/u) ? word.shown.replace(/[^\p{Extended_Pictographic}‍️]/gu, '') : '';

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try { await work(); } catch (e) { setError(e instanceof Error ? e.message : t('That did not save.')); } finally { setBusy(false); }
  }

  return (
    <li className="md-row">
      <span className="md-pic" aria-hidden="true">
        {custom?.image ? <img src={custom.image} alt="" /> : <span>{builtIn || '—'}</span>}
      </span>
      <span className="md-word">
        <strong>{word.label ?? word.term}</strong>
        <span className="field-hint">
          {[
            photos && (custom?.image ? t('Your photo') : t('Built-in picture')),
            voices && (custom?.audio ? t('Your voice') : t('Computer voice')),
          ].filter(Boolean).join(' · ')}
        </span>
        {error && <span className="error" role="alert">{error}</span>}
      </span>
      <span className="md-actions">
        <input
          ref={file}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void run(async () => saveImage(word.term, await shrinkImage(f), learnerId));
          }}
        />
        {photos && (
          <button type="button" className="btn secondary" disabled={busy} onClick={() => file.current?.click()}>
            {custom?.image ? t('Change photo') : t('Add photo')}
          </button>
        )}
        {photos && custom?.image && (
          <button type="button" className="btn ghost" disabled={busy} onClick={() => void run(() => removeMedia('image', word.term, learnerId))}>{t('Remove photo')}</button>
        )}
        {voices && (recording ? (
          <Recorder
            onCancel={() => setRecording(false)}
            onDone={(blob) => { setRecording(false); void run(() => saveAudio(word.term, blob, learnerId)); }}
          />
        ) : (
          <button type="button" className="btn secondary" disabled={busy} onClick={() => setRecording(true)}>
            {custom?.audio ? t('Record again') : t('Record voice')}
          </button>
        ))}
        {voices && <button type="button" className="btn ghost" onClick={() => { if (!playCustom(word.term)) void speak(word.label ?? word.term); }}>{t('Listen')}</button>}
        {voices && custom?.audio && (
          <button type="button" className="btn ghost" disabled={busy} onClick={() => void run(() => removeMedia('audio', word.term, learnerId))}>{t('Remove voice')}</button>
        )}
      </span>
    </li>
  );
}

export default function MediaDrawer({ words, learnerId, learnerName, onClose, talk = false }: {
  words: Word[];
  learnerId: string | null;
  learnerName: string | null;
  onClose: () => void;
  talk?: boolean;
}) {
  const t = useT();
  const entitlements = useEntitlements();
  const allowed = talk ? entitlements?.talkBoard : entitlements?.main;
  const [scope, setScope] = useState<'all' | 'learner'>('all');
  const scopeLearner = scope === 'learner' ? learnerId : null;
  const version = useMediaVersion();
  const [usage, setUsage] = useState<MediaUsage | null>(null);

  useEffect(() => { void loadMedia(learnerId, true); }, [learnerId]);
  useEffect(() => {
    if (!allowed) return;
    api<MediaUsage>('/media/usage', { token: true }).then(setUsage).catch(() => setUsage(null));
  }, [allowed, version]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="md-backdrop" onClick={onClose}>
      <aside className="md-drawer" role="dialog" aria-modal="true" aria-labelledby="md-title" onClick={(e) => e.stopPropagation()}>
        <header className="md-head">
          <h2 id="md-title">{talk ? t('Board photos & voice') : t('Custom pictures and voice')}</h2>
          <button type="button" className="btn ghost" onClick={onClose}>{t('Done')}</button>
        </header>
        {entitlements === null ? (
          <p className="muted">{t('Loading…')}</p>
        ) : !allowed ? (
          <div className="md-upsell">
            <p>{t('Use your own photos and your own voice instead of the built-in pictures and computer voice — a photo of the learner\'s own cup, a recording of mum saying "shoes on".')}</p>
            <p className="field-hint">{talk ? t('Your own photos and voice on the Talk Board come with Talk Board.') : t('Your own photos and voice come with every plan.')}</p>
            <Link className="btn" to="/account/subscription" onClick={onClose}>{t('See plans')}</Link>
          </div>
        ) : (
          <>
            <p className="field-hint md-intro">
              {talk
                ? t('Changes show straight away on the Talk Board.')
                : t('Changes show straight away, in this game and every other game that uses the same word.')}
            </p>
            {usage && <p className="field-hint">{usageLine(usage, t)}</p>}
            {learnerId && (
              <div className="md-scope" role="radiogroup" aria-label={t('Who these are for')}>
                <label><input type="radio" name="md-scope" checked={scope === 'all'} onChange={() => setScope('all')} /> {t('Every learner')}</label>
                <label><input type="radio" name="md-scope" checked={scope === 'learner'} onChange={() => setScope('learner')} /> {learnerName ? t('Only {name}', { name: learnerName }) : t('Only this learner')}</label>
              </div>
            )}
            {words.length === 0 ? (
              <p className="muted">{t('This game has no pictures or spoken words you can change.')}</p>
            ) : (
              <ul className="md-list">
                {words.map((w) => <Row key={w.term} word={w} learnerId={scopeLearner} />)}
              </ul>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
